import {openScheme} from '@xc/hiya-jsbridge'
import {operSystemEnum} from 'hiya-common/src/reducers/env'
import {http} from 'hiya-common/src/utils/axios'
import {HostMaps} from 'hiya-common/src/utils/host/AutoHost'
import {PROB_GIFT_API, PROB_GIFT_VISIT_KEY_PREFIX} from './constants'
import type {HomeResponse, MilestoneItem} from './types'

export type PlayEntryType = 1 | 2 | 3

export const PROB_GIFT_CREATE_ROOM_SCHEME = 'hiya://openCreateRoom'

/** 玩法页跳转 GlGift 活动页 scheme（other_info.from 原样用于进页埋点） */
export function buildGlGiftActOpenScheme(from: string): string {
  const otherInfo = encodeURIComponent(JSON.stringify({from}))
  const pageUrl = `${HostMaps.auto.version}/glgift`
  return `hiya://openWindow?fullscreen=0&need_user_info=1&other_info=${otherInfo}&url=${pageUrl}`
}

export function openGlGiftAct(from: string): void {
  openScheme(buildGlGiftActOpenScheme(from))
}

/** 进页埋点 localStorage key；无 activity_id 时用 default */
export function getProbGiftVisitStorageKey(configId: number): string {
  if (configId > 0) {
    return `${PROB_GIFT_VISIT_KEY_PREFIX}${configId}`
  }
  return `${PROB_GIFT_VISIT_KEY_PREFIX}default`
}

/**
 * 是否活动期间首次进页：localStorage 无标记则 true 并写入，否则 false。
 */
export function resolveIsFirstTimeVisit(configId: number): boolean {
  try {
    const storageKey = getProbGiftVisitStorageKey(configId)
    if (!window.localStorage.getItem(storageKey)) {
      window.localStorage.setItem(storageKey, '1')
      return true
    }
  } catch (e) {
    // localStorage 不可用时仅上报 false
  }
  return false
}

/** activity_id 有效时才并入埋点 data */
export function withProbGiftActivityId<T extends Record<string, unknown>>(
  data: T,
  configId: number
): T {
  if (configId > 0) {
    return {...data, activity_id: configId}
  }
  return data
}

/** 分页响应 offset：优先用服务端下一页偏移，否则 requestOffset + 本页条数 */
export function resolveNextPageOffset(
  requestOffset: number,
  pageListLength: number,
  responseOffset?: number
): number {
  if (responseOffset !== undefined && Number(responseOffset) > requestOffset) {
    return Number(responseOffset)
  }
  return requestOffset + pageListLength
}

/** getRoomId 桥接返回值归一 */
export function resolveRoomIdFromBridge(res: unknown): number {
  if (typeof res === 'number') {
    return res > 0 ? res : 0
  }
  if (res && typeof res === 'object') {
    const obj = res as {room_id?: unknown; roomId?: unknown}
    const id = Number(obj.room_id ?? obj.roomId ?? 0)
    return id > 0 ? id : 0
  }
  return 0
}

/** 玩法 / 弹窗 scheme 的 from 参数 */
export const PROB_GIFT_PLAY_SCHEME_FROM = {
  activity: 'activity',
  commonModal: 'commonmodal',
} as const

export type ProbGiftPlaySchemeFrom =
  typeof PROB_GIFT_PLAY_SCHEME_FROM[keyof typeof PROB_GIFT_PLAY_SCHEME_FROM]

/** 三玩法入口 scheme：room_id 来自 getRoomId 或接口回退；b_gift_id 仅盲盒使用 */
export function buildPlayEntryScheme(
  playType: PlayEntryType,
  roomId: number,
  bGiftId: number,
  from: ProbGiftPlaySchemeFrom = PROB_GIFT_PLAY_SCHEME_FROM.activity,
  isRoomOpen = false
): string {
  const wrapOpenRoom = (deeplink: string) =>
    `hiya://openRoom?room_id=${roomId}&close_current=1&deeplink_scheme=${encodeURIComponent(
      deeplink
    )}`
  const isIos =
    window.store.getState().env.deviceinfo?.h_dt === operSystemEnum.ios
  const useDirectFunnyGameScheme = isIos && isRoomOpen

  if (playType === 1) {
    const giftBoardScheme = `hiya://openGiftBoard?room_id=${roomId}&gift_id=${bGiftId}&is_to_bag=0&from=${from}`
    return useDirectFunnyGameScheme
      ? giftBoardScheme
      : wrapOpenRoom(giftBoardScheme)
  }
  if (playType === 2) {
    const gameScheme = `hiya://openFunnyGame?gameName=GoldenMiner&from=${from}`
    return useDirectFunnyGameScheme ? gameScheme : wrapOpenRoom(gameScheme)
  }
  const gameScheme = `hiya://openFunnyGame?gameName=LuckyBigWheel&from=${from}`
  return useDirectFunnyGameScheme ? gameScheme : wrapOpenRoom(gameScheme)
}

/** 通用弹窗等：先身份后拉主接口 */
export async function fetchProbGiftHome(): Promise<HomeResponse> {
  const resp = await http.post(
    PROB_GIFT_API.home,
    {
      ...window.store.getState().env.deviceinfo,
    },
    {store: window.store}
  )
  return resp.data.data
}

/** 里程碑开箱埋点 is_bonus：超过基础 5 档视为循环关 */
export function deriveIsBonus(
  level: number,
  milestones: MilestoneItem[]
): 0 | 1 {
  if (!milestones.length) {
    return 0
  }
  const baseCount = milestones.filter((item) => item.level <= 5).length
  if (baseCount <= 0) {
    return level > 5 ? 1 : 0
  }
  return level > baseCount ? 1 : 0
}

/** 解析 420 推送 data 中的 prize_pool */
export function parsePrizePoolFromPush(raw: unknown): number | null {
  let payload: unknown = raw
  if (typeof raw === 'string') {
    try {
      payload = JSON.parse(raw)
    } catch {
      return null
    }
  }
  if (!payload || typeof payload !== 'object') return null
  const prizePool = (payload as {prize_pool?: unknown}).prize_pool
  if (typeof prizePool !== 'number' || !Number.isFinite(prizePool)) return null
  return Math.max(0, Math.floor(prizePool))
}

export function formatPrizePool(value?: number): string {
  const n = Number.isFinite(value) ? Math.max(0, Math.floor(value || 0)) : 0
  return n.toLocaleString('en-US')
}
